// The data tier for the admin SPA.
//
// Replaces the deleted Next `app/api/*` route handlers. Every admin operation is
// now a Supabase Edge Function invoked with the signed-in user's session JWT
// (supabase-js attaches it automatically). Server-side, `requireAdmin` authorizes
// the call and the service-role work happens inside the function — no privileged
// key is ever present in this bundle.
//
//   const { places } = await invokeAdmin<{ places: PlaceRecord[] }>(
//     "admin_places_list", { q, limit: 50, offset });
//
// AWS-ops routes (pipeline control + CloudWatch logs) are NOT edge functions —
// they need an IAM identity — so they go through `invokeOps`, which targets the
// Phase-3 Lambda Function URL. Until that URL is configured those tabs surface a
// clear "not configured yet" error rather than silently failing.
import { supabase } from "./supabase";

/** Error carrying the edge function's / ops Lambda's HTTP status (when known). */
export class AdminApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
  }
}

/** Custom event dispatched on a 403 so the app shell can show a "not an admin" state. */
export const ADMIN_FORBIDDEN_EVENT = "admin:forbidden";

// Guard so a burst of concurrent 401s (every page fetching at once when a token
// dies) triggers exactly one sign-out + redirect.
let sessionExpiryHandled = false;

/**
 * Central auth-status interceptor (ADM-1). Runs before an AdminApiError is
 * thrown so a single code path fixes all 12 sections:
 *   - 401 (dead/expired session) → sign out + redirect to /login?reason=expired.
 *   - 403 (signed in but NOT an admin) → broadcast so the shell can render a
 *     dedicated "not an admin" state instead of a raw "Forbidden" banner.
 * The error is still thrown afterward so callers stop and pages keep working.
 */
function handleAuthStatus(status: number | undefined): void {
  if (typeof window === "undefined") return;
  if (status === 401) {
    if (sessionExpiryHandled) return;
    sessionExpiryHandled = true;
    // Fire-and-forget: clear the stale session, then bounce to login with a
    // reason the Login page reads to explain what happened.
    void supabase.auth.signOut?.().finally(() => {
      window.location.assign("/login?reason=expired");
    });
  } else if (status === 403) {
    window.dispatchEvent(new CustomEvent(ADMIN_FORBIDDEN_EVENT));
  }
}

interface FunctionsErrorLike {
  message: string;
  // supabase-js attaches the failed Response here on a non-2xx (FunctionsHttpError).
  context?: Response;
}

/**
 * Invoke an `admin_*` (or other login-gated) edge function and return its parsed
 * JSON body. Throws `AdminApiError` (carrying the edge fn's status + message) on
 * a non-2xx response or transport error.
 */
export async function invokeAdmin<T>(
  fn: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, {
    body: body ?? {},
  });

  if (error) {
    const err = error as FunctionsErrorLike;
    let status: number | undefined;
    let message = err.message;
    // FunctionsHttpError carries the original Response; pull the status + the
    // edge fn's `{ error }` message off it for a useful UI message.
    const ctx = err.context;
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        const parsed = (await ctx.clone().json()) as { error?: string };
        if (parsed?.error) message = parsed.error;
      } catch {
        // non-JSON body — keep the generic message
      }
    }
    handleAuthStatus(status);
    throw new AdminApiError(message, status);
  }

  return data as T;
}

/**
 * Call the Phase-3 AWS-ops Lambda (pipeline control + CloudWatch logs), passing
 * the user's session JWT as a bearer token (the Lambda re-verifies it + checks
 * is_admin). Throws a 503 `AdminApiError` until `VITE_ADMIN_OPS_URL` is set.
 */
export async function invokeOps<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const base = import.meta.env.VITE_ADMIN_OPS_URL;
  if (!base) {
    throw new AdminApiError(
      "AWS-ops service is not configured yet (Phase 3). Pipeline control and CloudWatch logs are unavailable until the ops Lambda is deployed.",
      503,
    );
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const res = await fetch(`${base.replace(/\/$/, "")}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const parsed = (await res.clone().json()) as { error?: string };
      if (parsed?.error) message = parsed.error;
    } catch {
      // keep generic
    }
    handleAuthStatus(res.status);
    throw new AdminApiError(message, res.status);
  }

  return (await res.json()) as T;
}

/** Normalize any thrown value to a display string for error banners. */
export function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
